import { ArrowLeft, ArrowRight, Check, Info, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../contasis/components/Button/Button";
import { normalizarAcotaciones, type PasoTour, type PosicionTour } from "./tiposTour";
import { usePanelFlotanteAbierto } from "@/shared/overlay/estadoPanelesFlotantes";
import { useEnfoqueTour, type CajaEnfoque } from "./useEnfoqueTour";
import "./tour.css";

interface TourFlotanteProps {
  paso: PasoTour | null;
  indicePaso: number;
  totalPasos: number;
  elementoObjetivo: HTMLElement | null;
  /**
   * Si la sección ya está completa, la tarjeta lo reconoce y el botón pasa
   * a "Continuar". Nunca bloquea el avance: reconocer sin exigir.
   */
  completado?: boolean;
  onSiguiente: () => void;
  onAtras: () => void;
  onOmitir: () => void;
  onFinalizar: () => void;
}

const ANCHO_TARJETA = 320;
const ALTO_TARJETA = 300;
const SEPARACION = 18;
const PADDING_VENTANA = 14;
// Coincide con la transición "opacity .25s ease" de .tour-capa/.tour-tarjeta en tour.css.
const TRANSICION_CIERRE_MS = 250;

type Posicion = { top: number; left: number };

interface InstantaneaTour {
  paso: PasoTour;
  indicePaso: number;
  totalPasos: number;
  elementoObjetivo: HTMLElement;
  caja: CajaEnfoque;
  fuera: boolean;
  completado: boolean;
}

function ubicarTarjeta(
  caja: CajaEnfoque,
  ancho: number,
  alto: number,
  preferida?: PosicionTour
): Posicion {
  const { innerWidth: vw, innerHeight: vh } = window;

  const colocar = (lado: PosicionTour): Posicion => {
    switch (lado) {
      case "izquierda":
        return { top: caja.top, left: caja.left - SEPARACION - ancho };
      case "arriba":
        return { top: caja.top - SEPARACION - alto, left: caja.left + caja.width - ancho };
      case "abajo":
        return { top: caja.top + caja.height + SEPARACION, left: caja.left };
      case "centro":
        return {
          top: caja.top + caja.height / 2 - alto / 2,
          left: caja.left + caja.width / 2 - ancho / 2,
        };
      case "derecha":
      default:
        return { top: caja.top, left: caja.left + caja.width + SEPARACION };
    }
  };

  let lado: PosicionTour = preferida ?? "derecha";
  let p = colocar(lado);

  // Si no cabe del lado preferido, se voltea antes de recortar.
  if (lado === "derecha" && p.left + ancho > vw - PADDING_VENTANA) p = colocar("abajo");
  if (lado === "izquierda" && p.left < PADDING_VENTANA) p = colocar("abajo");
  if (lado === "abajo" && p.top + alto > vh - PADDING_VENTANA) p = colocar("arriba");
  if (lado === "arriba" && p.top < PADDING_VENTANA) p = colocar("abajo");

  return {
    top: Math.min(Math.max(PADDING_VENTANA, p.top), Math.max(PADDING_VENTANA, vh - alto - PADDING_VENTANA)),
    left: Math.min(Math.max(PADDING_VENTANA, p.left), Math.max(PADDING_VENTANA, vw - ancho - PADDING_VENTANA)),
  };
}

export function TourFlotante({
  paso,
  indicePaso,
  totalPasos,
  elementoObjetivo,
  completado = false,
  onSiguiente,
  onAtras,
  onOmitir,
  onFinalizar,
}: TourFlotanteProps) {
  const refTarjeta = useRef<HTMLDivElement>(null);
  const [posicion, setPosicion] = useState<Posicion | null>(null);

  const { caja, fuera } = useEnfoqueTour(paso, elementoObjetivo, refTarjeta);
  const hayPanelAbierto = usePanelFlotanteAbierto();

  // El paso/caja se limpian a null en cuanto el tour se cierra, lo que antes
  // desmontaba la tarjeta de golpe. Guardamos el último estado válido y lo
  // mostramos desvanecido (mismo mecanismo que "data-oculto") durante la
  // transición, en vez de cortar la tarjeta de un frame a otro.
  const [cerrando, setCerrando] = useState(false);
  const instantaneaRef = useRef<InstantaneaTour | null>(null);
  const timeoutCierreRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (paso && elementoObjetivo && caja) {
      instantaneaRef.current = { paso, indicePaso, totalPasos, elementoObjetivo, caja, fuera, completado };
      if (cerrando) setCerrando(false);
      window.clearTimeout(timeoutCierreRef.current);
      return;
    }
    if (instantaneaRef.current && !cerrando) {
      setCerrando(true);
      timeoutCierreRef.current = window.setTimeout(() => {
        instantaneaRef.current = null;
        setCerrando(false);
      }, TRANSICION_CIERRE_MS);
    }
  }, [paso, indicePaso, totalPasos, elementoObjetivo, caja, fuera, completado, cerrando]);

  useEffect(() => () => window.clearTimeout(timeoutCierreRef.current), []);

  const datos: InstantaneaTour | null =
    paso && elementoObjetivo && caja
      ? { paso, indicePaso, totalPasos, elementoObjetivo, caja, fuera, completado }
      : instantaneaRef.current;

  const acotaciones = useMemo(() => normalizarAcotaciones(datos?.paso ?? null), [datos?.paso]);
  const pasos = useMemo(() => acotaciones.filter((a) => a.tipo === "paso"), [acotaciones]);
  const notas = useMemo(() => acotaciones.filter((a) => a.tipo === "nota"), [acotaciones]);

  const esUltimo = datos ? datos.indicePaso >= datos.totalPasos - 1 : false;
  const numero = datos ? String(datos.indicePaso + 1).padStart(2, "0") : "";

  useLayoutEffect(() => {
    if (!datos?.caja) {
      setPosicion(null);
      return;
    }
    const ancho = refTarjeta.current?.offsetWidth ?? ANCHO_TARJETA;
    const alto = refTarjeta.current?.offsetHeight ?? ALTO_TARJETA;
    setPosicion(ubicarTarjeta(datos.caja, ancho, alto, datos.paso?.posicion));
  }, [datos?.caja, datos?.paso?.posicion, acotaciones, datos?.completado]);

  useEffect(() => {
    if (!paso || hayPanelAbierto) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOmitir();
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [onOmitir, paso, hayPanelAbierto]);

  if (!datos) return null;

  const oculto = hayPanelAbierto || cerrando;
  const geometria = {
    top: datos.caja.top,
    left: datos.caja.left,
    width: datos.caja.width,
    height: datos.caja.height,
  };
  // Si la sección está pegada al tope, la pestaña baja al borde inferior.
  const pestanaAbajo = datos.caja.top < 40;

  return (
    <>
      {/* Velo + elevación */}
      <div className="tour-capa tour-velo" style={geometria} data-fuera={datos.fuera} data-oculto={oculto} aria-hidden="true" />

      {/* Filo de luz + pestaña de sección */}
      <div className="tour-capa tour-marco" style={geometria} data-fuera={datos.fuera} data-oculto={oculto} aria-hidden="true">
        {datos.paso.seccion && (
          <span className="tour-pestana" data-abajo={pestanaAbajo} data-completo={datos.completado}>
            {datos.completado && <Check size={11} strokeWidth={3.2} />}
            <span className="n">{numero}</span>
            {datos.paso.seccion}
          </span>
        )}
      </div>

      {/* Tarjeta */}
      <div
        ref={refTarjeta}
        className="tour-tarjeta"
        data-oculto={oculto}
        style={{ top: posicion?.top ?? PADDING_VENTANA, left: posicion?.left ?? PADDING_VENTANA }}
        role="dialog"
        aria-label="Ayuda guiada"
        aria-hidden={oculto || undefined}
      >
        {/* Metadatos: la X es hermana de la barra, no una capa encima */}
        <div className="mb-3 flex items-center gap-2.5">
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-surface-1 py-1 pl-1.5 pr-2.5">
            <b className="text-[10.5px] font-bold tabular-nums text-brand">{numero}</b>
            {datos.paso.seccion && (
              <span className="text-[10.5px] font-semibold text-muted">{datos.paso.seccion}</span>
            )}
          </span>

          <span className="tour-barra">
            {Array.from({ length: datos.totalPasos }, (_, i) => (
              <span
                key={i}
                className="tour-seg"
                data-estado={i < datos.indicePaso ? "hecho" : i === datos.indicePaso ? "actual" : "pendiente"}
              />
            ))}
          </span>

          <Button
            id="tour-cerrar"
            name="tourCerrar"
            iconOnly
            variant="ghost"
            size="sm"
            aria-label="Cerrar ayuda guiada"
            onClick={onOmitir}
            icon={<X size={14} />}
          />
        </div>

        <h3 className="font-display text-[16px] font-semibold leading-[1.35] tracking-[-0.02em] text-primary">
          {datos.paso.titulo}
        </h3>

        <p className="mt-1.5 text-[13px] leading-[1.55] text-tertiary">{datos.paso.descripcion}</p>

        {datos.completado && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[10px] font-bold text-success"
            style={{ background: "rgba(16,185,129,.10)" }}
            aria-live="polite"
          >
            <Check size={10} strokeWidth={3.2} />
            Ya completaste esta parte
          </div>
        )}

        {/* Sólo lo secuencial se numera */}
        {pasos.length > 0 && (
          <ol className="tour-pasos">
            {pasos.map((a, i) => (
              <li key={`${a.campo}-${i}`}>
                <span className="num">{String(i + 1).padStart(2, "0")}</span>
                <span>
                  {a.campo && (
                    <span className="block text-[12px] font-semibold text-secondary">{a.campo}</span>
                  )}
                  <span className="mt-px block text-[11.5px] leading-[1.5] text-tertiary">
                    {a.texto}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}

        {/* Información complementaria: fuera de la numeración */}
        {notas.length > 0 && (
          <div className="tour-nota">
            {notas.map((a, i) => (
              <div className="fila" key={`${a.campo}-${i}`}>
                <Info size={14} strokeWidth={2.2} />
                <span>
                  {a.campo && <span className="campo">{a.campo}: </span>}
                  {a.texto}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3.5 flex items-center justify-between gap-2">
          <Button
            id="tour-omitir"
            name="tourOmitir"
            variant="ghost"
            size="sm"
            onClick={onOmitir}
          >
            Omitir
          </Button>

          <div className="flex gap-1.5">
            {datos.indicePaso > 0 && (
              <Button
                id="tour-atras"
                name="tourAtras"
                iconOnly
                variant="secondary"
                size="sm"
                aria-label="Paso anterior"
                onClick={onAtras}
                icon={<ArrowLeft size={14} />}
              />
            )}
            <Button
              id={esUltimo ? "tour-finalizar" : "tour-siguiente"}
              name={esUltimo ? "tourFinalizar" : "tourSiguiente"}
              variant="primary"
              size="sm"
              icon={esUltimo ? <Check size={14} /> : <ArrowRight size={14} />}
              iconPosition="right"
              onClick={esUltimo ? onFinalizar : onSiguiente}
            >
              {datos.completado ? "Continuar" : esUltimo ? "Terminar" : "Siguiente"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
